import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import clsx from "clsx";
import { Modal } from "../../../../shared/ui/Modal";
import { Typography } from "../../../../shared/ui/Typography/Typography";
import styles from "./AddCompaniesUploadModal.module.scss";
import { UploadIcon, WarningCircleIcon, CaretDownIcon } from "@/frontend/shared/assets/icons";
import { SelectionDropdown } from "@/frontend/shared/ui/SelectionDropdown";
import { Input } from "@/frontend/shared/ui/Input";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import $api from "@/frontend/shared/api/axios";
import { useTagNames } from "../../model/getTags";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { DropdownInput } from "@/frontend/shared/ui/DropdownInput";
import { fireAnalyticsEvent } from "@/frontend/features/analytics";
import { EPostHogEventName } from "@/frontend/features/analytics/const/const";
import { Tag } from "@/frontend/shared/ui/TagV2";
import { CancelButton } from "@/frontend/shared/ui/Button/CancelButton/CancelButton";

interface AddCompaniesUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadFiles: (files: FileList, stageId?: string, tagNames?: string[]) => void;
  stageOptions: { label: string; value: string }[];
}

export function AddCompaniesUploadModal({ isOpen, onClose, uploadFiles, stageOptions }: AddCompaniesUploadModalProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [files, setFiles] = useState<FileList | null>(null);
  const [isStageSelectorOpen, setIsStageSelectorOpen] = useState<boolean>(false);
  const [isStageDropdownSelectorDown, setIsStageDropdownSelectorDown] = useState<boolean>(false);

  const [isTagDropdownSelectorDown, setIsTagDropdownSelectorDown] = useState<boolean>(false);
  const [tagsSelected, setTagsSelected] = useState<Record<string, boolean>>({});
  const [tagInput, setTagInput] = useState<string>("");

  const { data: tagNames, isLoading: isTagNamesLoading } = useTagNames();

  const initialSelectedStage = stageOptions?.length > 0 ? stageOptions?.[0]?.label : "";

  const [stageSelected, setStageSelected] = useState<string>(initialSelectedStage);

  const tagOptions =
    tagNames?.filter((t) => !tagsSelected?.[t.name])?.map((t) => ({ value: t.id, label: t.name as string })) ?? [];

  const tagsArray = Object.keys(tagsSelected);

  useEffect(() => {
    setStageSelected(initialSelectedStage);
  }, [initialSelectedStage]);

  const resetStates = () => {
    setFiles(null);
    setIsStageSelectorOpen(false);
    setIsStageDropdownSelectorDown(false);
    setStageSelected(initialSelectedStage ?? "");
    setIsTagDropdownSelectorDown(false);
    setTagsSelected({});
  };

  const handleDragEnter = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setIsStageSelectorOpen(true);
    setFiles(e.dataTransfer.files);
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setIsStageSelectorOpen(true);
      setFiles(e.target.files);
    }
  }, []);

  const _uploadFiles = async () => {
    if (!files) return;

    const stageId = stageOptions.find((s) => s.label === stageSelected)?.value;
    if (!stageId) return;

    const tagNamesSelected = Object.keys(tagsSelected);

    const dataTransfer = new DataTransfer();
    for (let i = 0; i < files.length; i++) {
      dataTransfer.items.add(files[i]);
    }

    // Deep copies - J
    const _files = dataTransfer.files;
    const _stageId = stageId;
    const _tagNamesSelected = [...tagNamesSelected];

    _onClose();
    await uploadFiles(_files, _stageId, _tagNamesSelected);
  };

  const _onClose = () => {
    resetStates();
    onClose?.();
  };

  function onClickUpload() {
    fireAnalyticsEvent(EPostHogEventName.ADD_COMPANIES);
    _uploadFiles();
  }

  const [docSendUrl, setDocSendUrl] = useState("");
  const [isDocSendModalOpen, setIsDocSendModalOpen] = useState(false);
  const [isProcessingDocSend, setIsProcessingDocSend] = useState(false);

  const lastUrlChangeTime = useRef<number>(0);
  const processedFile = useRef<{ file: File; docsendId: string } | null>(null);
  const processingPromise = useRef<Promise<any> | null>(null);

  const processDocSendUrl = async (url: string, showUI = true) => {
    const docsendId = url.split("/").pop();
    if (!docsendId) {
      if (showUI) snackbarConstructor("Invalid DocSend URL", "error");
      return null;
    }

    if (showUI) {
      setIsProcessingDocSend(true);
      snackbarConstructor("Processing DocSend link, this may take a minute...", "normal");
    }

    try {
      const response = await $api.post("/portfolio-companies/docsend-to-file", { docsendId }, { timeout: 120000 });

      if (!response.data?.success || !response.data?.file) {
        if (showUI) snackbarConstructor("Failed to process DocSend link", "error");
        return null;
      }

      const { base64, type, name } = response.data.file;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type });
      const file = new File([blob], name, { type });

      if (showUI) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        setFiles(dataTransfer.files);
        setIsDocSendModalOpen(false);
        setIsStageSelectorOpen(true);
        snackbarConstructor("DocSend presentation processed successfully", "normal");
      }

      return { file, docsendId };
    } catch (error: any) {
      if (showUI) {
        console.error("Error processing DocSend link:", error);
        if (error?.code === "ECONNABORTED") {
          snackbarConstructor(
            "Request timed out. The presentation may be too large or require authentication.",
            "error"
          );
        } else {
          snackbarConstructor(
            "Failed to process DocSend link. Please ensure the link is valid and accessible.",
            "error"
          );
        }
      }
      return null;
    } finally {
      if (showUI) setIsProcessingDocSend(false);
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setDocSendUrl(newUrl);

    // Check if it's a paste operation (quick change)
    const now = Date.now();
    const timeSinceLastChange = now - lastUrlChangeTime.current;
    lastUrlChangeTime.current = now;

    if (timeSinceLastChange < 50 && Math.abs(newUrl.length - docSendUrl.length) > 3) {
      // Cancel any existing processing
      processingPromise.current = null;
      processedFile.current = null;

      // Start background processing
      processingPromise.current = processDocSendUrl(newUrl, false).then((result) => {
        processedFile.current = result;
        return result;
      });
    }
  };

  const handleModalClose = () => {
    if (!isProcessingDocSend) {
      setDocSendUrl("");
      setIsDocSendModalOpen(false);
      processingPromise.current = null;
      processedFile.current = null;
    }
  };

  const handleAddClick = async () => {
    if (!docSendUrl) return;

    // If we have a processed file, use it
    if (processedFile.current?.file) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(processedFile.current.file);
      setFiles(dataTransfer.files);
      setIsDocSendModalOpen(false);
      setIsStageSelectorOpen(true);
      setDocSendUrl("");
      snackbarConstructor("DocSend presentation processed successfully", "normal");
      return;
    }

    // If we have a processing promise, wait for it
    if (processingPromise.current) {
      setIsProcessingDocSend(true);
      const result = await processingPromise.current;
      setIsProcessingDocSend(false);

      if (result?.file) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(result.file);
        setFiles(dataTransfer.files);
        setIsDocSendModalOpen(false);
        setIsStageSelectorOpen(true);
        setDocSendUrl("");
        snackbarConstructor("DocSend presentation processed successfully", "normal");
      }
      return;
    }

    // Otherwise process normally
    const result = await processDocSendUrl(docSendUrl, true);
    if (result) {
      setDocSendUrl("");
    }
  };

  // Add cleanup when modal closes
  useEffect(() => {
    if (!isDocSendModalOpen) {
      setDocSendUrl("");
      processingPromise.current = null;
      processedFile.current = null;
    }
  }, [isDocSendModalOpen]);

  const content = (
    <>
      <div className={styles.fileInputContainer}>
        <div className={styles.uploadContent}>
          <UploadIcon className={styles.uploadIcon} />
          <Typography variant="heading-7" color="greyscale900">
            Drag and drop or
            <br />
            <span className={styles.chooseText}>choose pitch decks</span> to upload
          </Typography>
          <Typography variant="body-3" color="greyscale600">
            PDF, PowerPoint PPTX or PPT files
          </Typography>
        </div>
        <label
          htmlFor="file-upload"
          className={clsx(styles.fileUpload, { [styles.dragOver]: isDragOver })}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input id="file-upload" type="file" className={styles.hidden} onChange={handleChange} multiple />
        </label>
      </div>
      <div className={styles.otherMethodsContainer}>
        <Typography variant="label-4">Other ways to upload</Typography>
        <div className={styles.otherMethods}>
          <NormalButton
            size="large"
            className={styles.otherButton}
            color="greyscale700"
            variant="secondary"
            onClick={() => setIsDocSendModalOpen(true)}
          >
            🔗 DocSend Link
          </NormalButton>
        </div>
        <div className={styles.warningContainer}>
          <WarningCircleIcon />
          <Typography variant="body-3" color="greyscale600">
            For optimal analysis, please ensure your pitch deck includes key elements such as the Executive Summary,
            Market Analysis, Business Model, and Financial Projections.
          </Typography>
        </div>
      </div>
    </>
  );

  return (
    <div>
      <Modal
        isOpen={isOpen}
        onClose={_onClose}
        title="Add Companies"
        isHeader
        width="454px"
        titleClassName={styles.titleClassName}
        titleId="addCompaniesModal-title"
        closeIconId="addCompaniesModal-closeButton"
      >
        {isStageSelectorOpen ? (
          <div>
            <div className={styles.selectionContainer}>
              <Typography variant="label-4" color="greyscale700" id="addCompaniesModal-stageLabel">
                Stage
              </Typography>
              <SelectionDropdown
                optionId="addCompaniesModal-stageDropdownOption"
                isMaxWidth
                className={styles.selectionDropdown}
                value={stageSelected}
                isOpen={isStageDropdownSelectorDown}
                onFocus={() => setIsStageDropdownSelectorDown((prev) => !prev)}
                onChange={(stageId) => {
                  const _stageName = stageOptions.find((s) => s.value === stageId);
                  setStageSelected(_stageName?.label ?? "");
                  setIsStageDropdownSelectorDown(false);
                }}
                onClose={() => setIsStageDropdownSelectorDown(false)}
                options={stageOptions}
                id="addCompaniesModal-stageDropdown"
              >
                <div className={styles.entryPointsContainer}>
                  <Typography variant="label-4" className={styles.entryPoints}>
                    {stageSelected}
                  </Typography>
                  <CaretDownIcon
                    className={clsx(styles.caretDownIcon, isStageDropdownSelectorDown && styles.rotateIcon)}
                  />
                </div>
              </SelectionDropdown>
            </div>

            <div className={styles.selectionContainer}>
              <Typography variant="label-4" color="greyscale700" id="addCompaniesModal-tagsLabel">
                Tags
              </Typography>
              <DropdownInput
                inputId="addCompaniesModal-tagsDropdown"
                optionId="addCompaniesModal-tagsDropdownOption"
                value={tagInput}
                className={styles.selectionDropdown}
                onChange={(e) => setTagInput(e?.target?.value ?? "")}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;

                  const tagsSelectedCopy = { ...tagsSelected };
                  tagsSelectedCopy[tagInput] = true;

                  setTagsSelected(tagsSelectedCopy);
                  setTagInput("");
                  setIsTagDropdownSelectorDown(false);
                }}
                options={tagOptions}
                onSelect={(tagName) => {
                  const _tagName = tagNames?.find((t) => t?.name?.toString() === tagName?.toString());
                  if (!_tagName?.name) return;

                  const tagsSelectedCopy = { ...tagsSelected };
                  tagsSelectedCopy[_tagName.name] = true;

                  setTagsSelected(tagsSelectedCopy);
                  setIsTagDropdownSelectorDown(false);
                }}
                onClose={() => setIsTagDropdownSelectorDown(false)}
                placeholder="Assign tags"
                description="Press enter to add tags"
              />

              <div className={styles.tagList} id="addCompaniesModal-tagList">
                {tagsArray?.map((tag, idx) => {
                  const key = `${tag}-${idx}`;
                  return (
                    <Tag
                      type="custom"
                      key={key}
                      onClose={() => {
                        const tagsSelectedV2Copy = { ...tagsSelected };
                        delete tagsSelectedV2Copy?.[tag];
                        setTagsSelected(tagsSelectedV2Copy);
                      }}
                    >
                      {tag}
                    </Tag>
                  );
                })}
              </div>
            </div>

            <div className={styles.optionContainer}>
              <CancelButton onClick={_onClose} id="addCompaniesModal-cancelButton">
                Cancel
              </CancelButton>
              <NormalButton size="large" onClick={onClickUpload} id="addCompaniesModal-confirmButton">
                Add Companies
              </NormalButton>
            </div>
          </div>
        ) : (
          <div>{content}</div>
        )}
      </Modal>
      <Modal isOpen={isDocSendModalOpen} onClose={handleModalClose} title="Add DocSend Link" isHeader width="480px">
        <div className={styles.docSendContainer}>
          <div className={clsx(styles.inputWrapper, { [styles.processing]: isProcessingDocSend })}>
            <Input
              value={docSendUrl}
              onChange={handleUrlChange}
              placeholder="Paste DocSend URL here"
              disabled={isProcessingDocSend}
            />
          </div>
          <Typography variant="body-3" color="greyscale500">
            Enter a DocSend URL to upload the presentation directly
          </Typography>
          <div className={styles.docSendButtons}>
            <NormalButton
              size="medium"
              onClick={handleAddClick}
              isSubmitting={isProcessingDocSend}
              isDisabled={isProcessingDocSend || !docSendUrl}
            >
              {isProcessingDocSend ? "Processing..." : "Add"}
            </NormalButton>
            <NormalButton size="medium" variant="tertiary" onClick={handleModalClose} isDisabled={isProcessingDocSend}>
              Cancel
            </NormalButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}
